import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';

export interface FrontmatterResult {
  data: Record<string, unknown> | null;
  /** Offset in `md` where the body begins (after the closing --- line). */
  bodyStart: number;
  /** Raw YAML text between the fences (no fences), '' when absent. */
  raw: string;
}

const FM_OPEN = /^---\r?\n/;

/** Parse a leading YAML frontmatter block. Tolerant: invalid YAML yields
 * data=null but still skips the block so the body offsets stay correct. */
export function parseFrontmatter(md: string): FrontmatterResult {
  const open = FM_OPEN.exec(md);
  if (!open) return { data: null, bodyStart: 0, raw: '' };
  const rest = md.slice(open[0].length);
  // Only consume trailing spaces/tabs on the fence line — NOT the following
  // blank lines (\s* would swallow them, so a property edit round-trip would
  // silently delete the conventional blank line before the body).
  const close = /^---[ \t]*(\r?\n|$)/m.exec(rest);
  if (!close) return { data: null, bodyStart: 0, raw: '' };
  const raw = rest.slice(0, close.index);
  const bodyStart = open[0].length + close.index + close[0].length;
  let data: Record<string, unknown> | null = null;
  try {
    const parsed = parseYaml(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      data = parsed as Record<string, unknown>;
    }
  } catch {
    data = null;
  }
  return { data, bodyStart, raw };
}

function asStringList(v: unknown): string[] {
  if (v == null) return [];
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (typeof v === 'string') {
    return v
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [String(v)];
}

export function aliasesFrom(data: Record<string, unknown> | null): string[] {
  if (!data) return [];
  return [...asStringList(data.aliases), ...asStringList(data.alias)];
}

export function fmTagsFrom(data: Record<string, unknown> | null): string[] {
  if (!data) return [];
  return [...asStringList(data.tags), ...asStringList(data.tag)].map((t) =>
    t.replace(/^#/, ''),
  );
}

/** Replace (or insert) the frontmatter block of a markdown document with the
 * given properties. Deletes the block entirely when props is empty. Returns
 * the new full markdown text. Used by the properties panel. */
export function withFrontmatter(md: string, props: Record<string, unknown>): string {
  const { bodyStart } = parseFrontmatter(md);
  const body = md.slice(bodyStart);
  const keys = Object.keys(props);
  if (keys.length === 0) return body;
  const yamlText = stringifyYaml(props).trimEnd();
  return `---\n${yamlText}\n---\n${body}`;
}
